/**
 * FinancePage — `/financeiro`: o fluxo de caixa da empresa.
 *
 * GRÁTIS no plano, com portão de PAPEL (owner/admin — `finance_entries` é
 * owner/admin também na RLS). A aba "Lucro" tem portão de PLANO PRÓPRIO
 * (`reports_advanced`), dentro dela: módulo grátis não abre relatório por
 * herança (#54).
 *
 * ─── O DESENHO OFICIAL NUMA TELA DE ABAS ────────────────────────────────────
 *
 * Hero sangrado + bilhete, o mesmo de `/quotes`, `/ordens-servico`, `/recibos` e
 * `/contratos`. O que precisou ser TRADUZIDO aqui:
 *
 * · O EYEBROW diz a FAMÍLIA ("Financeiro") e o H1 acompanha a ABA — exatamente
 *   o arranjo de `/quotes` (eyebrow "Documentos", h1 "Orçamentos"/"Vendas").
 *   Um h1 fixo em "Financeiro" com o total de "A pagar" embaixo seria a mentira
 *   do #182 uma linha acima do número.
 *
 * · O VALOR AO VIVO MUDA DE PERGUNTA POR ABA, e por isso ele é OPCIONAL (#199):
 *     Visão geral → Saldo do mês (a conta mora em `financeOverview`, junto com
 *                   os KPIs, para hero e cartão não poderem discordar);
 *     A receber /  → a soma do RECORTE visível (busca + status + período +
 *     A pagar        valor), com o rótulo dizendo de qual recorte ela saiu;
 *     Lucro       → NADA. O DRE tem período e base próprios, portão de PLANO
 *                   próprio e uma leitura de documentos que pode falhar — um
 *                   número no hero ali afirmaria lucro em `text-3xl` sem poder
 *                   garantir nenhuma das três coisas.
 *     Categorias  → NADA. Não há dinheiro nesta aba.
 *
 * · O RECORTE DAS LISTAS MORA AQUI, e não dentro da aba, pelo mesmo motivo que
 *   o período do Lucro já morava: o hero soma o que a lista mostra, então os
 *   dois têm de sair do MESMO cálculo (duas implementações discordariam na
 *   primeira correção, sem erro nenhum — #82/#69). O efeito colateral é
 *   desejável: o recorte sobrevive à troca de aba, e voltar para uma aba com o
 *   filtro zerado parece bug.
 *
 * · As ABAS continuam `<Tabs>` (azul, herdado do componente base — #25): aqui
 *   elas SÃO troca de contexto, diferente do status das listas, que é recorte e
 *   virou faixa de botões planos.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ArrowLeft,
  ArrowUpDown,
  Check,
  DollarSign,
  FileSpreadsheet,
  Lock,
  Plus,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/hooks/useRole';
import { useXlsxExport } from '@/hooks/useXlsxExport';
import { useFinanceCategories, useFinanceEntriesRaw } from '@/hooks/useFinance';
import { buildFinanceXlsx } from '@/lib/xlsxTemplates';
import { FinanceOverviewTab } from '@/components/finance/FinanceOverviewTab';
import { FinanceListTab } from '@/components/finance/FinanceListTab';
import { FinanceCategoriesTab } from '@/components/finance/FinanceCategoriesTab';
import { FinanceProfitTab } from '@/components/finance/FinanceProfitTab';
import type { DateRange } from '@/components/reports/DateRangeFilter';
import type { PaymentDateBasis } from '@/lib/paymentsReport';
import { subMonths } from 'date-fns';
import type { FinanceEntryPrefill } from '@/pages/FinanceEntryFormPage';
import { BoletoScannerButton } from '@/components/quotes/BoletoScannerButton';
import type { FinanceEntryWithStatus, FinanceKind } from '@/types/database';
import {
  DEFAULT_FINANCE_SORT,
  FINANCE_PERIOD_LABELS,
  FINANCE_PERIOD_PRESETS,
  FINANCE_SORT_LABELS,
  FINANCE_SORT_ORDER,
  countActiveFinanceFilters,
  countFinanceEntriesByStatus,
  financeEntryMatchesSearch,
  financeListTotalLabel,
  financeStatusFilterOptions,
  isDeadFinanceStatusFilter,
  matchesFinancePeriod,
  matchesFinanceStatus,
  matchesFinanceValue,
  resolveFinancePeriod,
  sortFinanceEntriesBy,
  summarizeFinanceList,
  type FinancePeriodPreset,
  type FinanceSortOption,
  type FinanceStatusFilter,
} from '@/lib/financeListFilters';
import { summarizeFinanceMonth } from '@/lib/financeOverview';
import {
  FILTER_CHIP_COMPACT,
  FILTER_CHIP_OFF,
  FILTER_CHIP_ON,
  FILTER_CHIP_ROW,
} from '@/components/filters/filterChips';
import { ListFiltersDialog, type FilterGroup } from '@/components/filters/ListFiltersDialog';
import { EMPTY_LIST_RANGE, type ListValueRange } from '@/lib/listFilters';
import { todayIsoDate } from '@/lib/financeStatus';
import { formatBRL } from '@/lib/financeLabels';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { plural } from '@/lib/plural';
import { cn } from '@/lib/utils';

type TabValue = 'overview' | 'receivable' | 'payable' | 'profit' | 'categories';

/** O h1 do hero acompanha a aba (ver o cabeçalho). */
const TAB_TITLES: Record<TabValue, string> = {
  overview: 'Visão geral',
  receivable: 'A receber',
  payable: 'A pagar',
  profit: 'Lucro',
  categories: 'Categorias',
};

const PERIOD_GROUP_KEY = 'period';

export default function FinancePage() {
  const navigate = useNavigate();
  // Só LEITURA: o atalho `?new=true` navega e não reescreve a URL (ver abaixo).
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Finance is sensitive: only owner/admin of the active company may see it.
  const { canSeeFinance } = useRole();

  const [tab, setTab] = useState<TabValue>('overview');
  // Estado da aba Lucro. Mora aqui (e não dentro da aba) para o período e a base
  // sobreviverem à troca de aba — trocar de aba e voltar zerando o filtro
  // parece bug.
  const [profitRange, setProfitRange] = useState<DateRange>(() => ({
    from: subMonths(new Date(), 6),
    to: new Date(),
  }));
  const [profitBasis, setProfitBasis] = useState<PaymentDateBasis>('paid');

  // Recorte das listas ("A receber"/"A pagar"). Ver o cabeçalho: mora aqui
  // porque o hero soma exatamente o que a lista mostra.
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<FinanceStatusFilter>('all');
  const [sortOption, setSortOption] = useState<FinanceSortOption>(DEFAULT_FINANCE_SORT);
  const [periodPreset, setPeriodPreset] = useState<FinancePeriodPreset | null>(null);
  const [valueRange, setValueRange] = useState<ListValueRange>(EMPTY_LIST_RANGE);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { entries, isLoading } = useFinanceEntriesRaw();

  // Refetch when an offline write (or another tab) touches finance data.
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ['finance-entries', user?.id] });
    };
    window.addEventListener('finance-updated', handler);
    return () => window.removeEventListener('finance-updated', handler);
  }, [queryClient, user?.id]);

  // O formulário é uma ROTA: o prefill viaja no state da navegação.
  const openNew = (kind: FinanceKind) => {
    navigate('/financeiro/novo', { state: { prefill: { kind }, defaultKind: kind } });
  };

  const openEdit = (entry: FinanceEntryWithStatus) => {
    navigate(`/financeiro/${entry.id}/editar`);
  };

  const isListTab = tab === 'receivable' || tab === 'payable';
  const listKind: FinanceKind = tab === 'payable' ? 'expense' : 'income';
  const defaultKind: FinanceKind = listKind;

  /**
   * Atalho "Lançamento" da navegação inferior (`/financeiro?new=true`).
   *
   * 🔴 Aqui NÃO se limpa o parâmetro depois de navegar, e isso é a correção de
   * um defeito: `setSearchParams` navega para a rota CORRENTE sem o parâmetro,
   * então chamá-lo logo depois do `navigate` desfazia a navegação — o atalho
   * deixava o usuário parado em `/financeiro`, sem formulário nenhum e sem erro
   * nenhum. Limpar a URL só faz sentido quando a ação abre um MODAL na própria
   * rota (é o caso de `/recibos` e `/agenda`).
   */
  useEffect(() => {
    if (user && canSeeFinance && searchParams.get('new') === 'true') {
      navigate('/financeiro/novo', {
        replace: true,
        state: { prefill: { kind: defaultKind }, defaultKind },
      });
    }
  }, [user, canSeeFinance, searchParams, navigate, defaultKind]);

  /**
   * A faixa do período é resolvida UMA vez por render, e não dentro do
   * `filter`: ela deriva de "agora", e chamá-la por lançamento compararia cada
   * linha contra um "agora" ligeiramente diferente.
   */
  const periodRange = useMemo(
    () => (periodPreset ? resolveFinancePeriod(periodPreset) : null),
    [periodPreset],
  );
  const today = todayIsoDate();

  /** O recorte SEM o status — é ele que alimenta as contagens dos botões. */
  const entriesBeforeStatus = useMemo(
    () =>
      entries.filter(
        (entry) =>
          entry.kind === listKind &&
          financeEntryMatchesSearch(entry, search) &&
          matchesFinancePeriod(entry, periodRange) &&
          matchesFinanceValue(entry, valueRange),
      ),
    [entries, listKind, search, periodRange, valueRange],
  );

  const statusCounts = useMemo(
    () => countFinanceEntriesByStatus(entriesBeforeStatus, today),
    [entriesBeforeStatus, today],
  );

  const visible = useMemo(
    () =>
      sortFinanceEntriesBy(
        entriesBeforeStatus.filter((entry) => matchesFinanceStatus(entry, statusFilter, today)),
        sortOption,
      ),
    [entriesBeforeStatus, statusFilter, sortOption, today],
  );

  /**
   * `includeDead` liga quando o recorte É o status morto: com o filtro em
   * "Cancelados", excluir todo mundo faria o hero anunciar R$ 0,00 com a lista
   * cheia embaixo.
   */
  const listSummary = summarizeFinanceList(visible, {
    includeDead: isDeadFinanceStatusFilter(statusFilter),
    today,
  });
  const monthSummary = useMemo(() => summarizeFinanceMonth(entries), [entries]);
  const activeModalFilters = countActiveFinanceFilters(periodPreset, valueRange);
  const recorteAtivo =
    search.trim().length > 0 || statusFilter !== 'all' || activeModalFilters > 0;

  const statusOptions = financeStatusFilterOptions(listKind);

  const filterGroups: FilterGroup[] = [
    {
      key: PERIOD_GROUP_KEY,
      label: 'Vencimento',
      options: [
        // "Qualquer data" é uma opção EXPLÍCITA, e não a ausência de seleção:
        // sem ela, quem escolheu um período não teria como voltar atrás.
        { value: '', label: 'Qualquer data' },
        ...FINANCE_PERIOD_PRESETS.map((preset) => ({
          value: preset,
          label: FINANCE_PERIOD_LABELS[preset],
        })),
      ],
    },
  ];

  if (!user) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <DollarSign className="mb-4 h-16 w-16 text-muted-foreground/50" />
          <h2 className="text-xl font-semibold">Financeiro</h2>
          <p className="mb-4 text-muted-foreground">Faça login para acessar o financeiro</p>
          <Button3D onClick={() => navigate('/auth')}>Entrar</Button3D>
        </div>
      </MainLayout>
    );
  }

  // Role gate: members/viewers of a company cannot access its finances, even
  // by navigating directly. (RLS also blocks the data; this is the UI guard.)
  if (!canSeeFinance) {
    return (
      <MainLayout>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
              <Lock className="h-7 w-7 text-muted-foreground" />
            </div>
            <h2 className="text-lg font-semibold">Acesso restrito</h2>
            <p className="mt-1 max-w-xs text-sm text-muted-foreground">
              O módulo financeiro está disponível apenas para o proprietário e administradores
              desta empresa.
            </p>
            <Button variant="outline" className="mt-5" onClick={() => navigate('/dashboard')}>
              Voltar ao início
            </Button>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  /**
   * Os controles do recorte das listas. Função (chamada), não componente
   * declarado aqui dentro: um componente definido no corpo de outro é
   * REMONTADO a cada render, e o campo de busca perderia o foco a cada tecla.
   */
  const renderListControls = () => (
    <>
      {/* Busca + ordenação + filtros. Os dois ícones são `shrink-0` e o campo é
          `min-w-0 flex-1`: sem isso o input empurraria os botões para fora da
          tela em vez de encolher (#186). */}
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Descrição do lançamento"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Ordenação: menu, não modal. São as DUAS direções que a agenda admite
            — ordenar por valor desmontaria o agrupamento por dia de vencimento,
            que é a estrutura da tela (ver `financeListFilters`). */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-xl"
              aria-label={`Ordenar (${FINANCE_SORT_LABELS[sortOption]})`}
              title={`Ordenar: ${FINANCE_SORT_LABELS[sortOption]}`}
            >
              <ArrowUpDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {FINANCE_SORT_ORDER.map((option) => (
              <DropdownMenuItem key={option} onSelect={() => setSortOption(option)} className="gap-2">
                {/* O CHECK ocupa o mesmo espaço quando ausente: sem o
                    placeholder, o rótulo escolhido fica deslocado. */}
                <span className="flex h-4 w-4 items-center justify-center">
                  {sortOption === option && <Check className="h-4 w-4 text-primary" />}
                </span>
                {FINANCE_SORT_LABELS[option]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Filtros: aqui SIM um modal — são controles independentes que fazem
            sentido escolhidos juntos, e o rascunho do diálogo é o que impede a
            lista de se reorganizar a cada tecla do campo de valor. */}
        <Button
          variant="outline"
          size="icon"
          className="relative h-10 w-10 shrink-0 rounded-xl"
          onClick={() => setFiltersOpen(true)}
          aria-label={
            activeModalFilters > 0
              ? `Filtros (${plural(activeModalFilters, 'ativo', 'ativos')})`
              : 'Filtros'
          }
          title="Filtros"
        >
          <SlidersHorizontal className="h-4 w-4" />
          {activeModalFilters > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-white">
              {activeModalFilters}
            </span>
          )}
        </Button>
      </div>

      {/* STATUS — botões planos, não alternador (#25/#188). A contagem vai no
          `title`/`aria-label`, onde não custa pixel (#191). */}
      <div className={FILTER_CHIP_ROW}>
        {statusOptions.map((option) => {
          const active = statusFilter === option.value;
          // A contagem responde "quantos eu veria se clicasse aqui" — por isso
          // sai do recorte SEM o status. Um número que não bate com a lista
          // depois do clique é pior do que número nenhum.
          const count = statusCounts[option.value] ?? 0;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => setStatusFilter(option.value)}
              aria-pressed={active}
              aria-label={`${option.label} (${count})`}
              title={`${option.label}: ${plural(count, 'lançamento', 'lançamentos')}`}
              className={cn(FILTER_CHIP_COMPACT, active ? FILTER_CHIP_ON : FILTER_CHIP_OFF)}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {/* A ORDENAÇÃO ativa é a única decisão da tela que não se anuncia sozinha:
          status tem botões e período/valor têm o selo do ícone. É RÓTULO, não
          botão — o controle é o ícone logo acima. */}
      {!isLoading && visible.length > 0 && (
        <div className="flex items-center gap-1.5 px-1 text-sm text-muted-foreground">
          <ArrowUpDown className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{FINANCE_SORT_LABELS[sortOption]}</span>
        </div>
      )}
    </>
  );

  return (
    <MainLayout>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — o desenho oficial do app                                     */}
        {/* ================================================================== */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          <div className="relative flex items-center gap-2">
            {/* `/financeiro` não está na navegação inferior: sem este botão, a
                única saída da tela é o gesto do sistema. */}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
              onClick={() => navigate('/dashboard')}
              aria-label="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Financeiro</p>
              <h1 className="line-clamp-2 text-lg font-bold leading-tight">{TAB_TITLES[tab]}</h1>
            </div>

            {/* Ações SECUNDÁRIAS viram ícone (#186): com texto elas disputariam
                a linha com o título, e sem `flex-wrap` o excedente não quebra —
                ele sai da tela, levando junto a ação PRIMÁRIA, que é a última.
                `aria-label`/`title` são obrigatórios aqui (#142). */}
            {tab === 'payable' && (
              <BoletoScannerButton
                enableFinanceEntry
                iconOnly
                className="relative h-9 w-9 shrink-0 rounded-full bg-white/15 p-0 text-white hover:bg-white/25 hover:text-white"
              />
            )}
            <FinanceExportButton />
            {tab !== 'categories' && tab !== 'profit' && (
              /* A ação PRIMÁRIA mantém o texto — é a que menos pode sumir. */
              <Button3D
                size="sm"
                color="light"
                className="shrink-0"
                onClick={() => openNew(defaultKind)}
                aria-label="Novo lançamento"
                title="Novo lançamento"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Novo
              </Button3D>
            )}
          </div>

          {/* O VALOR AO VIVO é OPCIONAL (#199): "Lucro" e "Categorias" não têm
              número que o hero possa afirmar sem mentir — ver o cabeçalho. */}
          {tab === 'overview' && (
            <div className="relative mt-5 min-w-0">
              <p className="text-xs text-white/75">Saldo do mês</p>
              <p
                className={cn(
                  'truncate font-bold leading-tight',
                  heroTotalSizeClass(formatBRL(monthSummary.balance)),
                )}
              >
                {formatBRL(monthSummary.balance)}
              </p>
              <p className="mt-0.5 text-xs text-white/70">
                {formatBRL(monthSummary.income)} em entradas ·{' '}
                {formatBRL(monthSummary.expense)} em saídas
                {/* "no silent caps": o que ficou fora da conta é DITO. */}
                {monthSummary.excludedCount > 0 &&
                  ` · ${plural(
                    monthSummary.excludedCount,
                    'cancelado fora da conta',
                    'cancelados fora da conta',
                  )}`}
              </p>
            </div>
          )}

          {isListTab && (
            <div className="relative mt-5 min-w-0">
              <p className="text-xs text-white/75">{financeListTotalLabel(listKind, statusFilter)}</p>
              {/* `heroTotalSizeClass` é a FONTE ÚNICA do degrau de tamanho
                  (#159-b): escrever uma regra local aqui seria mais uma cópia de
                  uma medida que já divergiu entre telas uma vez. */}
              <p
                className={cn(
                  'truncate font-bold leading-tight',
                  heroTotalSizeClass(formatBRL(listSummary.total)),
                )}
              >
                {formatBRL(listSummary.total)}
              </p>
              <p className="mt-0.5 text-xs text-white/70">
                {/* Enquanto a lista carrega, o hero NÃO afirma que está vazia:
                    um "Nenhum lançamento" que aparece antes da resposta é uma
                    afirmação falsa em cima de um R$ 0,00 igualmente falso. */}
                {listSummary.count === 0
                  ? isLoading
                    ? 'Carregando…'
                    : 'Nenhum lançamento neste filtro'
                  : plural(listSummary.count, 'lançamento', 'lançamentos')}
                {listSummary.excludedCount > 0 &&
                  ` · ${plural(
                    listSummary.excludedCount,
                    'cancelado fora da conta',
                    'cancelados fora da conta',
                  )}`}
              </p>
            </div>
          )}
        </div>

        {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes. */}
        <div className="relative z-10 -mt-9 space-y-5">
          <Tabs value={tab} onValueChange={(v) => setTab(v as TabValue)}>
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview" className="text-[11px]">Visão geral</TabsTrigger>
              <TabsTrigger value="receivable" className="text-[11px]">A receber</TabsTrigger>
              <TabsTrigger value="payable" className="text-[11px]">A pagar</TabsTrigger>
              <TabsTrigger value="profit" className="text-[11px]">Lucro</TabsTrigger>
              <TabsTrigger value="categories" className="text-[11px]">Categorias</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-5">
              <FinanceOverviewTab onEdit={openEdit} />
            </TabsContent>

            {/* Controles e lista são um GRUPO só (todos respondem "o que eu
                estou vendo"), com `space-y-2` — e a lista fica DENTRO dele, e
                não como irmã com `-mt-N`: margem negativa num filho direto
                perde a briga de especificidade contra o `space-y-*` do
                container (#194). */}
            <TabsContent value="receivable" className="mt-5">
              <div className="space-y-2">
                {renderListControls()}
                <FinanceListTab
                  kind="income"
                  entries={visible}
                  isLoading={isLoading}
                  recorteAtivo={recorteAtivo}
                  onEdit={openEdit}
                />
              </div>
            </TabsContent>

            <TabsContent value="payable" className="mt-5">
              <div className="space-y-2">
                {renderListControls()}
                <FinanceListTab
                  kind="expense"
                  entries={visible}
                  isLoading={isLoading}
                  recorteAtivo={recorteAtivo}
                  onEdit={openEdit}
                />
              </div>
            </TabsContent>

            <TabsContent value="profit" className="mt-5">
              <FinanceProfitTab
                dateRange={profitRange}
                onDateRangeChange={setProfitRange}
                basis={profitBasis}
                onBasisChange={setProfitBasis}
              />
            </TabsContent>

            <TabsContent value="categories" className="mt-5">
              <FinanceCategoriesTab />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Filtros: vencimento e faixa de valor.
          O STATUS aparece só na faixa de botões, e não aqui, porque ele já se
          anuncia sozinho na tela — repeti-lo no modal duplicaria o controle sem
          revelar nada que o usuário não esteja vendo. */}
      <ListFiltersDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        description="Recorte os lançamentos por vencimento e por valor."
        groups={filterGroups}
        rangeLabel="Valor do lançamento"
        value={{ groups: { [PERIOD_GROUP_KEY]: periodPreset ?? '' }, range: valueRange }}
        onApply={(next) => {
          const wanted = next.groups[PERIOD_GROUP_KEY];
          setPeriodPreset(
            wanted && FINANCE_PERIOD_PRESETS.includes(wanted as FinancePeriodPreset)
              ? (wanted as FinancePeriodPreset)
              : null,
          );
          setValueRange(next.range);
        }}
      />
    </MainLayout>
  );
}

/**
 * Export button for the finance module. Exporting remains independently gated
 * by the `xls_export` feature inside useXlsxExport.
 */
function FinanceExportButton() {
  const { entries } = useFinanceEntriesRaw();
  const { categories } = useFinanceCategories();
  const { exportXlsx } = useXlsxExport();

  const handleExport = () => {
    exportXlsx(() => buildFinanceXlsx(entries, categories), 'financeiro', {
      emptyMessage: 'Nenhum lançamento para exportar',
    });
  };

  return (
    // Ação secundária no hero: ícone, com o nome no `aria-label`/`title` (#142).
    <Button
      variant="ghost"
      size="icon"
      onClick={handleExport}
      disabled={entries.length === 0}
      className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white disabled:opacity-40"
      aria-label="Exportar"
      title="Exportar"
    >
      <FileSpreadsheet className="h-4 w-4" />
    </Button>
  );
}
